import type { Dispatch, FunctionComponent, SetStateAction } from 'react'
import { useState } from 'react'
import { ButtonLink, Grid, GridItem, TypographyV2 } from '@which/seatbelt'
import { CrossIcon } from '@which/seatbelt/src/components/Icons/Navigational'

import classNames from 'classnames'

import styles from './WinbackOfferBanner.module.scss'

type CloseButtonProps = {
  setShowBanner: Dispatch<SetStateAction<boolean>>
}

const CloseButton: FunctionComponent<CloseButtonProps> = ({ setShowBanner }) => {
  const closeBanner = () => {
    const cookieName = 'resub-banner-closed'

    document.cookie = `${cookieName}=1; path=/`
    setShowBanner(false)
  }

  const closeBannerOnKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      closeBanner()
    }
  }

  return (
    <button
      onClick={closeBanner}
      onKeyDown={closeBannerOnKeyDown}
      data-testid="closeButton"
      data-which-id="resubscribe-offer-banner-button"
      data-card-name="banner close"
      data-personalisation="true"
      className={styles.winbackBannerClose}
      aria-label="Close this resubscribe banner"
    >
      <CrossIcon width={15} height={15} data-which-id="resubscribe-offer-banner-close" />
    </button>
  )
}

export const WinbackOfferBanner: FunctionComponent = () => {
  const [showBanner, setShowBanner] = useState<boolean>(true)
  const sectionTitle = '50% off when you resubscribe'
  const buttonText = 'Rejoin today'

  if (!showBanner) {
    return null
  }

  return (
    <Grid
      className={classNames('w-page-wrapper', styles.winbackBanner)}
      data-testid="winback-banner"
    >
      <GridItem columnStart={{ large: 2 }} span={{ large: 10 }} className={styles.winbackBannerBox}>
        <CloseButton setShowBanner={setShowBanner} />
        <Grid includeGutters={false} className={styles.winbackBannerContent}>
          <GridItem span={{ small: 2, medium: 10, large: 10 }} className={styles.winbackBannerText}>
            <TypographyV2 textStyle="sb-text-heading-medium" tag="h1">
              Come back for half the price
            </TypographyV2>

            <TypographyV2
              textStyle="sb-text-heading-small"
              tag="p"
              className={styles.winbackBannerOffer}
            >
              {sectionTitle}
            </TypographyV2>

            <TypographyV2 textStyle="sb-text-body-default-regular" tag="p">
              Your subscription has ended. Rejoin from just{' '}
              <span className={styles.winbackBannerStruckPrice}>£79</span> £39.50 for your first
              year to keep getting trusted advice.
            </TypographyV2>

            <TypographyV2 textStyle="sb-text-body-x-small-regular" tag="p">
              Renews at £79. Cancel anytime.
            </TypographyV2>
          </GridItem>

          <GridItem
            columnStart={{ small: 1, medium: 11, large: 11 }}
            span={{ small: 2, medium: 2, large: 2 }}
            className={styles.winbackBannerButtonWrapper}
          >
            <ButtonLink
              href="https://join.which.co.uk/join/subscribe/promo"
              className={styles.winbackBannerButton}
              data-which-id="resubscribe-offer-banner-button"
              data-section={sectionTitle}
              data-item-price="39.50"
              data-card-name={buttonText}
              data-item-group="resubscribe offer banner"
              data-personalisation="true"
            >
              {buttonText}
            </ButtonLink>
          </GridItem>
        </Grid>
      </GridItem>
    </Grid>
  )
}
